import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Timeline,
  AccountTree,
  Refresh,
  Fullscreen,
} from '@mui/icons-material';
import FundAUMChart from './FundAUMChart';
import RiskDistributionChart from './RiskDistributionChart';
import RegulatoryTimelineChart from './RegulatoryTimelineChart';
import FundNetworkVisualization from '../visualization/FundNetworkVisualization';
import {
  Fund,
  RegulatoryUpdate,
  FundNetworkData,
} from '../../services/dashboardService';

interface DashboardChartsProps {
  funds: Fund[];
  regulatoryUpdates: RegulatoryUpdate[];
  networkData: FundNetworkData;
  onRefresh?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chart-tabpanel-${index}`}
      aria-labelledby={`chart-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const DashboardCharts: React.FC<DashboardChartsProps> = ({
  funds,
  regulatoryUpdates,
  networkData,
  onRefresh,
}) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [fullscreenChart, setFullscreenChart] = useState<number | null>(null);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleFullscreen = (chartIndex: number) => {
    setFullscreenChart(fullscreenChart === chartIndex ? null : chartIndex);
  };

  const charts = [
    {
      label: 'Fund AUM',
      icon: <BarChartIcon />,
      component: <FundAUMChart funds={funds} />,
    },
    {
      label: 'Risk Distribution',
      icon: <PieChartIcon />,
      component: <RiskDistributionChart funds={funds} />,
    },
    {
      label: 'Regulatory Timeline',
      icon: <Timeline />,
      component: <RegulatoryTimelineChart updates={regulatoryUpdates} />,
    },
    {
      label: 'Fund Network',
      icon: <AccountTree />,
      component: <FundNetworkVisualization networkData={networkData} />,
    },
  ];

  const renderChart = (chartIndex: number) => {
    const chart = charts[chartIndex];
    return (
      <Card
        sx={{
          height: fullscreenChart === chartIndex ? '80vh' : 'auto',
          minHeight: fullscreenChart === chartIndex ? '80vh' : 400,
        }}
      >
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {chart.icon}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {chart.label}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              {onRefresh && (
                <Tooltip title="Refresh data">
                  <IconButton size="small" onClick={onRefresh}>
                    <Refresh />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={fullscreenChart === chartIndex ? "Exit fullscreen" : "Fullscreen"}>
                <IconButton
                  size="small"
                  onClick={() => handleFullscreen(chartIndex)}
                >
                  <Fullscreen />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {chart.component}
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (fullscreenChart !== null) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: 'background.default',
          zIndex: theme.zIndex.modal,
          p: 2,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {charts[fullscreenChart].label} - Fullscreen View
          </Typography>
          <IconButton onClick={() => setFullscreenChart(null)}>
            <Fullscreen />
          </IconButton>
        </Box>
        {renderChart(fullscreenChart)}
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Analytics & Visualizations
        </Typography>
        {onRefresh && (
          <Tooltip title="Refresh all charts">
            <IconButton onClick={onRefresh} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Chart Tabs */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="dashboard charts"
            variant="scrollable"
            scrollButtons="auto"
          >
            {charts.map((chart, index) => (
              <Tab
                key={index}
                label={chart.label}
                icon={chart.icon}
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            ))}
          </Tabs>
        </Box>

        {charts.map((chart, index) => (
          <TabPanel key={index} value={activeTab} index={index}>
            {chart.component}
          </TabPanel>
        ))}
      </Card>

      {/* Quick View Grid */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Quick View
      </Typography>
      <Grid container spacing={3}>
        {charts.map((chart, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card
              sx={{
                height: 300,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#16a34a',
                },
              }}
              onClick={() => setActiveTab(index)}
            >
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {chart.icon}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {chart.label}
                    </Typography>
                  </Box>
                  <Tooltip title="View full chart">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveTab(index);
                      }}
                    >
                      <Fullscreen />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  {/* Mini version of the chart */}
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    bgcolor="background.default"
                    borderRadius={1}
                    sx={{ border: '1px dashed', borderColor: 'divider' }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Click to view {chart.label.toLowerCase()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardCharts;
